import * as React from "react"
import { Slot } from "@radix-ui/react-slot"
import { cva, type VariantProps } from "class-variance-authority"

import { cn } from "@/lib/utils"

/* Editorial 2026 button system.
 * - All variants use canonical tokens (--accent-brick, --fg-cream, --panel,
 *   --border-hairline, --bg-canvas). No legacy --brick-* / --bg-* / --text-*.
 * - Square corners (rounded-sm) match the editorial visual rhythm.
 * - `premium` is kept as an alias for the default terracotta CTA — the old
 *   gradient/glow treatment is gone (flat accent per editorial system). */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-sm text-[13px] font-medium tracking-[-0.01em] transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--accent-brick))] focus-visible:ring-offset-2 focus-visible:ring-offset-[hsl(var(--bg-canvas))] disabled:pointer-events-none disabled:opacity-50 [&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default:
          "bg-[hsl(var(--accent-brick))] text-[hsl(var(--bg-canvas))] hover:bg-[hsl(var(--accent-brick-hover))]",
        premium:
          "bg-[hsl(var(--accent-brick))] text-[hsl(var(--bg-canvas))] hover:bg-[hsl(var(--accent-brick-hover))]",
        glass:
          "bg-[hsl(var(--panel))] border border-[hsl(var(--border-hairline))] text-[hsl(var(--fg-cream))] hover:border-[hsl(var(--accent-brick))]",
        magnetic:
          "bg-[hsl(var(--accent-brick))] text-[hsl(var(--bg-canvas))] hover:bg-[hsl(var(--accent-brick-hover))]",
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive/90",
        outline:
          "border border-[hsl(var(--border-hairline))] bg-transparent text-[hsl(var(--fg-cream))] hover:border-[hsl(var(--accent-brick))] hover:text-[hsl(var(--accent-brick))]",
        secondary:
          "bg-[hsl(var(--panel))] text-[hsl(var(--fg-cream))] border border-[hsl(var(--border-hairline))] hover:bg-[hsl(var(--panel))]/80",
        ghost:
          "bg-transparent text-[hsl(var(--fg-cream))] hover:text-[hsl(var(--accent-brick))]",
        link:
          "bg-transparent text-[hsl(var(--accent-brick))] underline-offset-4 hover:underline hover:text-[hsl(var(--accent-brick-hover))]",
      },
      size: {
        default: "h-9 px-5",
        sm: "h-8 rounded-sm px-3 text-[12px]",
        lg: "h-11 rounded-sm px-8 text-[14px]",
        icon: "h-9 w-9",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
)

interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : "button"
    return (
      <Comp
        className={cn(buttonVariants({ variant, size, className }))}
        ref={ref}
        {...props}
      />
    )
  }
)
Button.displayName = "Button"

export { Button }
